import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, TextField, Button, CircularProgress } from '@mui/material';
import { Logo } from '../assets/images';
import { RightArrow } from '../assets/icons';
import { useNavigate } from 'react-router-dom';
import AuthService from '../services/auth.service';

const LoginOTP = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [showOTPInput, setShowOTPInput] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        client_email: '',
        otp: '',
    });
    const [otpSession, setOTPSession] = useState('');

    const [timeLeft, setTimeLeft] = useState(30);
    const [canResend, setCanResend] = useState(false);

    const resendTimer = useRef<any>(null);

    useEffect(() => {
        startTimer()
        return () => clearInterval(resendTimer.current);
    }, []);

    const startTimer = () => {
        resendTimer.current = setInterval(() => {
            setTimeLeft((prevTime) => {
                if (prevTime <= 0) {
                    clearInterval(resendTimer.current);
                    setCanResend(true);
                    return 0;
                }
                return prevTime - 1;
            });
        }, 1000);
    }
    
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSendOTP = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!formData.email) {
            setError('Please enter your email');
            return;
        }

        setError('');
        setIsLoading(true);
        setCanResend(false);
        setTimeLeft(30);
        startTimer();
        
        try {
            console.log(formData)
            const otpres = await AuthService.sendLoginOTP(formData.email + '@easywebinar.com');
            setShowOTPInput(true);
            if(otpres && otpres.data && otpres.data.session) setOTPSession(otpres.data.session)
            if (formData.otp) {
                setFormData(prev => ({ ...prev, otp: '' }));
            }
        } catch (err: any) {
            setError(err.message || 'Failed to send OTP');
            setCanResend(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyOTP = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!formData.otp) {
            setError('Please enter the OTP');
            return;
        }

        setError('');
        setIsLoading(true);

        try {
            await AuthService.verifyLoginOTP({
                email: formData.email + '@easywebinar.com',
                client_email: formData.client_email,
                otp: formData.otp,
                session: otpSession
            });
            navigate('/dashboard');
        } catch (err: any) {
            setError(err.message || 'Invalid OTP');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Box p={{ xs: "16px 16px 11px", sm: "18px 20px 16px", md: "24px 20px 16px" }} sx={{ background: "#F5F6F8" }} display="flex" justifyContent="space-between" alignItems="center" mt={{ xs: "", sm: "unset" }} position="sticky" top="0" zIndex="2">
                <Box display="flex"><img src={Logo} alt="logo" /></Box>
                <Button
                    variant="text"
                    onClick={() => navigate('/login')}
                    sx={{
                        textTransform: "capitalize",
                        color: "#111827 !important",
                        fontSize: "min(calc(14px + 4 * (100vw - 400px) / (1920 - 400)), 16px)",
                        display: "flex",
                        gap: "8px",
                        "&:hover": {
                            boxShadow: "none!important",
                            borderRadius: "8px",
                            "& svg": {
                                transform: "translateX(2px)",
                                transition: "all 0.3s ease",
                            }
                        }
                    }}
                >
                    Log in
                    <RightArrow />
                </Button>
            </Box>

            <Box p={{ xs: "16px", sm: "20px", xl: "0 48px 0" }} sx={{ background: "#F5F6F8", backgroundSize: "cover", backgroundPosition: "center" }} display="flex" alignItems="center" justifyContent="center" minHeight={{ xs: "calc(100vh - 63px)", sm: "calc(100vh - 80px)" }}>
                <Box maxWidth="576px" width="100%">
                    <Box p={{ xs: "20px", sm: "40px" }} display="flex" flexDirection="column" justifyContent="center" alignItems="center" boxShadow="0px 4px 8px 0px #0000000D" border="1px solid #E9E9E9" bgcolor="#FFFFFF" margin="auto" borderRadius="24px" zIndex={{ sm: "2" }} position={{ md: "relative" }}>
                        <Typography
                            color="#060023"
                            fontWeight="700"
                            fontSize={{ xs: "min(calc(18px + 10 * (100vw - 400px) / (1920 - 400)), 24px)", sm: "24px" }}
                        >
                            OTP Login
                        </Typography>
                        <Typography variant="body1" color="#666666" mb="8px" mt="8px" fontSize={{ xs: "min(calc(14px + 4 * (100vw - 400px) / (1920 - 400)), 14px)" }} textAlign="center">
                            {showOTPInput ? 'Please enter the verification code sent to your email' : 'Please enter your email to receive the verification code'}
                        </Typography>

                        <Box component="form" sx={{ width: "100%", mt: 2 }} onSubmit={showOTPInput ? handleVerifyOTP : handleSendOTP}>
                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                                <TextField
                                    fullWidth
                                    id="email"
                                    name="email"
                                    label="Email"
                                    value={formData.email}
                                    onChange={(e) => {
                                        const value = e.target.value.replace('@easywebinar.com', '');
                                        const event = {
                                            target: {
                                                value: value,
                                                name: 'email',
                                                id: 'email',
                                                type: 'text'
                                            }
                                        } as React.ChangeEvent<HTMLInputElement>;
                                        handleInputChange(event);
                                    }}
                                    disabled={showOTPInput}
                                    error={!!error}
                                    helperText={error}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: '#E8EAF0',
                                            },
                                        },
                                    }}
                                />
                                <Typography 
                                    color="#666666"
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        fontSize: { 
                                            xs: "min(calc(14px + 4 * (100vw - 400px) / (1920 - 400)), 14px)"
                                        }
                                    }}
                                >
                                    @easywebinar.com
                                </Typography>
                            </Box>
                            
                            {showOTPInput && (
                                <>
                                    <TextField
                                        fullWidth
                                        id="client_email"
                                        name="client_email"
                                        label="Client Email"
                                        value={formData.client_email}
                                        onChange={handleInputChange}
                                        sx={{
                                            mb: 2,
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: '#E8EAF0',
                                                },
                                            },
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        id="otp"
                                        name="otp"
                                        label="Enter OTP"
                                        value={formData.otp}
                                        onChange={handleInputChange}
                                        error={!!error}
                                        sx={{
                                            mb: 2,
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: '#E8EAF0',
                                                },
                                            },
                                        }}
                                    />
                                </>
                            )}

                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                disabled={isLoading}
                                disableElevation
                                sx={{
                                    textTransform: "capitalize",
                                    height: "51px",
                                    fontSize: "16px",
                                    fontWeight: "500",
                                    bgcolor: "#0382F7 !important",
                                    "&:hover": { bgcolor: "#006dd1 !important" }
                                }}
                            >
                                {isLoading ? 'Processing...' : (showOTPInput ? 'Verify OTP' : 'Send Code')}
                            </Button>
                        </Box>
                        
                        {showOTPInput && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                {canResend ? (
                                    <Typography
                                        component="button"
                                        type="button"
                                        onClick={(e) => handleSendOTP(e)}
                                        sx={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#0382F7',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            '&:hover': {
                                                textDecoration: 'underline'
                                            }
                                        }}
                                    >
                                        Resend OTP
                                    </Typography>
                                ) : (
                                    <Typography color="#666666" fontSize="14px">
                                        Resend OTP in {timeLeft}s
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default LoginOTP;